<template>
  <div class="video-content">
    <div class="full-width-container">
      <!-- 视频生成列表 -->
      <div class="video-list">
        <!-- 新生成的视频（优先显示） -->
        <div class="video-item" v-for="(item, index) in newVideoItems" :key="'new-' + index">
          <!-- 模型名称和时间 -->
          <div class="video-header">
            <div class="video-info-badges">
              <div class="model-badge">
                <i class="icon">⚙️</i> {{ item.model }}
              </div>
              <div class="info-badge">
                {{ item.size || item.duration }}
              </div>
              <div class="type-badge">
                {{ item.type === 'text-to-video' ? '文生视频' : item.type === 'digital-human' ? '数字人' : '图生视频' }}
              </div>
            </div>
            <div class="generate-time">
              {{ item.generateTime }}
            </div>
          </div>
          
          <!-- 图生视频布局 -->
          <div v-if="item.type === 'image-to-video'" class="content-layout">
            <!-- 左侧：上传图片 -->
            <div class="image-section">
              <div class="media-box" :class="getImageClass(item.imageAspect)">
                <div class="image-container">
                  <img 
                    v-if="item.uploadedImage || item.thumbnail" 
                    :src="item.uploadedImage || item.thumbnail" 
                    alt="参考图片" 
                    class="preview-image"
                  >
                  <div v-else class="no-images">
                    <div class="upload-icon">📁</div>
                    <p>无参考图片</p>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- 中间箭头 -->
            <div class="arrow-section">
              <div class="arrow">→</div>
            </div>
            
            <!-- 右侧：视频 -->
            <div class="video-section">
              <div class="media-box">
                <!-- 生成中状态 - 统一加载样式 -->
                <div v-if="item.status === 'generating'" class="loading-container">
                  <div class="glass-effect">
                    <div class="glass-shine"></div>
                  </div>
                  <div class="loading-content">
                    <div class="loading-spinner"></div>
                    <div class="loading-text">正在生成视频...</div>
                    <div class="progress-bar-container">
                      <div class="progress-bar">
                        <div class="progress-fill" :style="{ width: (item.progress || 0) + '%' }"></div>
                      </div>
                      <span class="progress-text">{{ Math.round(item.progress || 0) }}%</span>
                    </div>
                  </div>
                  </div>
                <video v-else-if="item.status === 'completed'" :src="item.videoUrl" controls class="result-video"></video>
              </div>
            </div>
          </div>
          
          <!-- 文生视频布局 -->
          <div v-else-if="item.type === 'text-to-video'" class="text-content-layout">
            <div class="text-video-section">
              <div class="media-box">
                <div v-if="item.status === 'generating'" class="loading-container">
                  <div class="glass-effect">
                    <div class="glass-shine"></div>
                  </div>
                  <div class="loading-content">
                    <div class="loading-spinner"></div>
                    <div class="loading-text">正在生成视频...</div>
                    <div class="progress-bar-container">
                      <div class="progress-bar">
                        <div class="progress-fill" :style="{ width: (item.progress || 0) + '%' }"></div>
                      </div>
                      <span class="progress-text" v-if="item.progress < 100">{{ Math.round(item.progress || 0) }}%</span>
                      <span class="progress-text error" v-else>生成失败，请尝试更换模型</span>
                    </div>
                  </div>
                </div>
                <div v-else-if="item.status === 'failed'" class="failed-container">
                  <div class="failed-icon">❌</div>
                  <div class="failed-text">生成失败，请尝试更换模型</div>
                </div>
                <video v-else-if="item.status === 'completed'" :src="item.videoUrl" controls class="result-video"></video>
              </div>
            </div>
          </div>
          
          <!-- 数字人生成布局 -->
          <div v-else-if="item.type === 'digital-human'" class="content-layout">
            <div class="image-section">
              <div class="media-box" :class="getImageClass(item.imageAspect)">
                <div class="image-container">
                  <img 
                    v-if="item.uploadedImage || item.thumbnail" 
                    :src="item.uploadedImage || item.thumbnail" 
                    alt="数字人头像" 
                    class="preview-image"
                  />
                  <div v-else class="no-images">
                    <div class="upload-icon">🎭</div>
                    <p>没有头像</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="arrow-section">
              <div class="arrow">→</div>
            </div>
            
            <div class="video-section">
              <div class="media-box">
                <div class="image-container">
                  <div v-if="item.status === 'generating'" class="loading-container">
                    <div class="glass-effect">
                      <div class="glass-shine"></div>
                    </div>
                    <div class="loading-content">
                      <div class="loading-spinner"></div>
                      <div class="loading-text">正在生成视频...</div>
                      <div class="progress-bar-container">
                        <div class="progress-bar">
                          <div class="progress-fill" :style="{ width: (item.progress || 0) + '%' }"></div>
                        </div>
                        <span class="progress-text" v-if="item.progress < 100">{{ Math.round(item.progress || 0) }}%</span>
                        <span class="progress-text error" v-else>生成失败，请尝试重新提交或更换模型！</span>
                      </div>
                    </div>
                  </div>
                  <div v-else-if="item.status === 'failed'" class="failed-container">
                    <div class="failed-icon">❌</div>
                    <div class="failed-text">生成失败，请尝试更换模型</div>
                  </div>
                  <video v-else-if="item.status === 'completed'" :src="item.videoUrl" controls class="result-video"></video>
                </div>
              </div>
            </div>
          </div>
          
          <!-- 提示词和功能按钮区域 -->
          <div class="prompt-container">
            <div class="prompt-content">
              <div class="prompt">{{ item.prompt }}</div>
            </div>
            <div class="action-buttons">
              <button class="action-btn" @click="copyPrompt(item.prompt)" title="复制提示词">
                <i class="icon">📋</i>
              </button>
              <button class="action-btn" @click="regenerateVideo(item)" title="重新生成">
                <i class="icon">🔄</i>
              </button>
              <button class="action-btn" @click="editVideo(item)" title="重新编辑">
                <i class="icon">✏️</i>
              </button>
              <button class="action-btn" @click="downloadVideo(item)" title="下载">
                <i class="icon">⬇️</i>
              </button>
              <button class="action-btn delete" @click="deleteVideo(index, true)" title="删除">
                <i class="icon">🗑️</i>
              </button>
            </div>
          </div>
        </div>
        
        <!-- 历史生成的视频（后置加载） -->
        <div class="video-item" v-for="(item, index) in historyVideoItems" :key="'history-' + item.id">
          <!-- 模型名称和时间 -->
          <div class="video-header">
            <div class="video-info-badges">
              <div class="model-badge">
                <i class="icon">⚙️</i> {{ item.model }}
              </div>
              <div class="info-badge">
                {{ item.size || item.duration }}
              </div>
              <div class="type-badge">
                {{ item.type === 'text-to-video' ? '文生视频' : item.type === 'digital-human' ? '数字人' : '图生视频' }}
              </div>
            </div>
            <div class="generate-time">
              {{ item.generateTime }}
            </div>
          </div>
          
          <!-- 图生视频布局 -->
          <div v-if="item.type === 'image-to-video'" class="content-layout">
            <div class="image-section">
              <div class="media-box" :class="getImageClass(item.imageAspect)">
                <div class="image-container">
                  <img 
                    v-if="item.uploadedImage || item.thumbnail" 
                    :src="item.uploadedImage || item.thumbnail" 
                    alt="参考图片" 
                    class="preview-image"
                  >
                  <div v-else class="no-images">
                    <div class="upload-icon">📁</div>
                    <p>无参考图片</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="arrow-section">
              <div class="arrow">→</div>
            </div>
            
            <div class="video-section">
              <div class="media-box">
                <video v-if="item.status === 'completed'" :src="item.videoUrl" controls class="result-video"></video>
              </div>
            </div>
          </div>
          
          <!-- 文生视频布局 -->
          <div v-else-if="item.type === 'text-to-video'" class="text-content-layout">
            <div class="text-video-section">
              <div class="media-box">
                <video v-if="item.status === 'completed'" :src="item.videoUrl" controls class="result-video"></video>
              </div>
            </div>
          </div>
          
          <!-- 数字人生成布局 -->
          <div v-else-if="item.type === 'digital-human'" class="content-layout">
            <div class="image-section">
              <div class="media-box" :class="getImageClass(item.imageAspect)">
                <div class="image-container">
                  <img 
                    v-if="item.uploadedImage || item.thumbnail" 
                    :src="item.uploadedImage || item.thumbnail" 
                    alt="数字人头像" 
                    class="preview-image"
                  />
                  <div v-else class="no-images">
                    <div class="upload-icon">🎭</div>
                    <p>没有头像</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="arrow-section">
              <div class="arrow">→</div>
            </div>
            
            <div class="video-section">
              <div class="media-box">
                <div class="image-container">
                  <div v-if="item.status === 'generating'" class="loading-container">
                    <div class="glass-effect">
                      <div class="glass-shine"></div>
                    </div>
                    <div class="loading-content">
                      <div class="loading-spinner"></div>
                      <div class="loading-text">正在生成视频...</div>
                      <div class="progress-bar-container">
                        <div class="progress-bar">
                          <div class="progress-fill" :style="{ width: (item.progress || 0) + '%' }"></div>
                        </div>
                        <span class="progress-text" v-if="item.progress < 100">{{ Math.round(item.progress || 0) }}%</span>
                        <span class="progress-text error" v-else>生成失败，请尝试重新提交或更换模型！</span>
                      </div>
                    </div>
                  </div>
                  <div v-else-if="item.status === 'failed'" class="failed-container">
                    <div class="failed-icon">❌</div>
                    <div class="failed-text">生成失败，请尝试重新提交或更换模型！</div>
                  </div>
                  <video v-else-if="item.status === 'completed'" :src="item.videoUrl" controls class="result-video"></video>
                </div>
              </div>
            </div>
          </div>
          
          <!-- 提示词和功能按钮区域 -->
          <div class="prompt-container">
            <div class="prompt-content">
              <div class="prompt">{{ item.prompt }}</div>
            </div>
            <div class="action-buttons">
              <button class="action-btn" @click="copyPrompt(item.prompt)" title="复制提示词">
                <i class="icon">📋</i>
              </button>
              <button class="action-btn" @click="regenerateVideo(item)" title="重新生成">
                <i class="icon">🔄</i>
              </button>
              <button class="action-btn" @click="editVideo(item)" title="重新编辑">
                <i class="icon">✏️</i>
              </button>
              <button class="action-btn" @click="downloadVideo(item)" title="下载">
                <i class="icon">⬇️</i>
              </button>
              <button class="action-btn delete" @click="deleteVideo(index, false)" title="删除">
                <i class="icon">🗑️</i>
              </button>
            </div>
          </div>
        </div>
        
        <!-- 空状态 -->
        <div v-if="newVideoItems.length === 0 && historyVideoItems.length === 0 && !isLoadingHistory" class="empty-state">
          <div class="empty-icon">🎥</div>
          <h3>还没有生成过视频</h3>
          <p>使用下面的工具栏开始生成您的第一个视频吧！</p>
        </div>

        <!-- 广告位 -->
        <AdBanner position="video-generate" />
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import historyCache from '@/utils/historyCache'
import AdBanner from '@/components/AdBanner.vue'

export default {
  name: 'VideoContent',
  components: {
    AdBanner
  },
  props: {
    params: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      newVideoItems: [],
      historyVideoItems: [],
      isLoadingHistory: false,
      isLoaded: false
    }
  },
  mounted() {
    this.loadHistory()
  },
  watch: {
    params: {
      handler(newParams) {
        if (newParams && Object.keys(newParams).length > 0) {
          this.generateFromParams(newParams)
        }
      },
      deep: true
    }
  },
  methods: {
    async loadHistory() {
      if (this.isLoaded) return
      this.isLoaded = true
      
      // 延迟加载历史数据，让新生成的内容优先显示
      await new Promise(resolve => setTimeout(resolve, 500))
      
      // 始终从数据库加载最新数据，确保新生成的内容不会丢失
      this.isLoadingHistory = true
      
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
      const user_id = userInfo.id || 0
      
      try {
        const response = await axios.get('/api/video-history', {
          params: { user_id: user_id }
        })
        
        // 处理响应数据 - 可能是直接数组或包装在响应中
        let data = response.data
        if (data && data.data) {
          data = data.data
        }
        
        // 确保 data 是数组
        if (!Array.isArray(data)) {
          data = []
        }
        
        // 更新缓存（仅用于降级）
        historyCache.updateModuleHistory('video', data)
        
        // 将后端数据格式转换为前端期望的格式，存入历史列表
        this.historyVideoItems = this.formatHistoryData(data)
      } catch (error) {
        console.error('加载视频历史失败:', error)
        // 数据库加载失败时才使用缓存作为降级
        if (historyCache.isCacheValid() && historyCache.hasModuleData('video')) {
          console.log('数据库加载失败，使用缓存作为降级')
          const cachedData = historyCache.getModuleHistory('video')
          this.historyVideoItems = this.formatHistoryData(cachedData)
        } else {
          this.historyVideoItems = []
        }
      } finally {
        this.isLoadingHistory = false
      }
    },
    
    async loadVideoHistory() {
      console.log('=== VideoContent: loadVideoHistory 强制刷新 ===')
      this.isLoadingHistory = true
      
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
      const user_id = userInfo.id || 0
      
      try {
        const response = await axios.get('/api/video-history', {
          params: { user_id: user_id }
        })
        
        console.log('收到视频历史响应:', response.data)
        
        let data = response.data
        if (data && data.data) {
          data = data.data
        }
        
        if (!Array.isArray(data)) {
          data = []
        }
        
        historyCache.updateModuleHistory('video', data)
        this.historyVideoItems = this.formatHistoryData(data)
        
        console.log('刷新后的历史视频数量:', this.historyVideoItems.length)
      } catch (error) {
        console.error('刷新视频历史失败:', error)
        this.historyVideoItems = []
      } finally {
        this.isLoadingHistory = false
      }
    },
    
    formatHistoryData(data) {
      return data.map(item => ({
        id: item.id,
        type: item.referenceImage ? 'image-to-video' : 'text-to-video',
        prompt: item.prompt || '',
        model: item.model || '',
        duration: item.duration || '',
        size: item.size || item.resolution || '',
        imageAspect: item.aspect || item.size || '16:9',
        uploadedImage: item.referenceImage || '',
        videoUrl: item.videoUrl || '',
        thumbnail: item.thumbnail || '',
        status: item.videoUrl ? 'completed' : 'generating',
        progress: item.videoUrl ? 100 : 0,
        progressText: item.videoUrl ? '生成完成' : '正在生成...',
        generateTime: item.generateTime || item.createdAt || item.created_at || ''
      }))
    },
    generateFromParams(params) {
      // 转换类型名称
      let videoType = 'text-to-video'
      if (params.type) {
        if (params.type === 'text') {
          videoType = 'text-to-video'
        } else if (params.type === 'image') {
          videoType = 'image-to-video'
        } else if (params.type === 'avatar') {
          videoType = 'digital-human'
        } else {
          videoType = params.type
        }
      }
      
      // 准备生成的数据
      const newVideo = {
        type: videoType,
        prompt: params.prompt || '',
        model: params.model || 'Seedance 2.0',
        duration: params.duration || '5秒',
        size: params.size || '16:9',
        imageAspect: params.size || '16:9',
        // 图生视频用referenceImage，数字人用avatarImage
        uploadedImage: videoType === 'image-to-video' 
          ? (params.hasReferenceImage ? sessionStorage.getItem('videoReferenceImage') : null)
          : (videoType === 'digital-human' 
              ? (params.hasAvatarImage ? sessionStorage.getItem('videoAvatarImage') : null)
              : null),
        videoUrl: '',
        thumbnail: params.thumbnail || `https://trae-api-cn.mchost.guru/api/ide/v1/text-to-image?prompt=${encodeURIComponent(params.prompt || 'video')}&image_size=square_hd`,
        progress: 0,
        progressText: '正在生成...',
        status: 'generating',
        generateTime: this.getCurrentTime()
      }
      
      // 添加到新生成列表
      this.newVideoItems.unshift(newVideo)
      
      // 模拟生成过程
      this.simulateGeneration(newVideo)
    },
    getCurrentTime() {
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      const hours = String(now.getHours()).padStart(2, '0')
      const minutes = String(now.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}`
    },
    getImageClass(aspect) {
      if (!aspect) return {}
      // 如果是宽高比格式如 "16:9"，直接使用
      if (aspect.includes(':')) {
        return {
          'aspect-16-9': aspect === '16:9',
          'aspect-9-16': aspect === '9:16',
          'aspect-1-1': aspect === '1:1'
        }
      }
      // 如果是分辨率格式如 "1920x1080"，推断比例
      const match = aspect.match(/(\d+)x(\d+)/)
      if (!match) return {}
      
      const width = parseInt(match[1])
      const height = parseInt(match[2])
      const ratio = width / height
      
      if (ratio > 1.7) {
        return { 'aspect-16-9': true }
      } else if (ratio < 0.6) {
        return { 'aspect-9-16': true }
      } else {
        return { 'aspect-1-1': true }
      }
    },
    copyPrompt(prompt) {
      navigator.clipboard.writeText(prompt).then(() => {
        console.log('提示词已复制')
      })
    },
    regenerateVideo(item) {
      console.log('重新生成:', item)
    },
    editVideo(item) {
      console.log('编辑:', item)
    },
    async deleteVideo(index, isNew) {
      if (!confirm('确定要删除这个视频吗？')) {
        return
      }
      
      try {
        if (isNew) {
          this.newVideoItems.splice(index, 1)
          console.log('✅ 已删除新生成的视频')
        } else {
          const item = this.historyVideoItems[index]
          console.log('🗑️ 准备删除视频:', item)
          
          if (item.id) {
            const response = await fetch(`/api/video-history/${item.id}`, {
              method: 'DELETE',
              headers: { 'Content-Type': 'application/json' }
            })
            
            const data = await response.json()
            console.log('📤 删除响应:', data)
            
            if (data.code === 200) {
              console.log('✅ 数据库删除成功')
            } else {
              console.warn('⚠️ 数据库删除失败，但继续移除前端显示')
            }
          }
          
          this.historyVideoItems.splice(index, 1)
        }
      } catch (error) {
        console.error('删除失败:', error)
      }
    },
    downloadVideo(item) {
      console.log('下载:', item)
      if (item.videoUrl) {
        const link = document.createElement('a')
        link.href = item.videoUrl
        link.download = `video-${item.generateTime}.mp4`
        link.click()
      } else {
        console.log('视频还在生成中或下载链接无效')
      }
    },
    
    addNewVideo(newVideo) {
      // 添加到新生成列表最前面
      this.newVideoItems.unshift(newVideo)
      
      // 如果是generating状态，模拟进度条
      if (newVideo.status === 'generating') {
        this.simulateGeneration(newVideo)
      }
    },
    simulateGeneration(video) {
      setInterval(() => {
        video.progress += 2.5
        if (video.progress >= 100) {
          video.progress = 100
          video.progressText = '生成中...'
          // 不自动完成，保持玻璃特效状态，等待真实的视频URL
        } else {
          video.progressText = `正在生成... 预计剩余${Math.ceil((100 - video.progress) / 2.5) * 2}秒`
        }
      }, 200)
    },
    
    updateVideoProgress(data) {
      if (this.newVideoItems.length > 0) {
        const firstVideo = this.newVideoItems[0]
        if (firstVideo.status === 'generating') {
          firstVideo.progress = data.progress || 0
        }
      }
    },
    
    updateVideoResult(videoData) {
      console.log('=== VideoContent: updateVideoResult ===')
      console.log('videoData:', videoData)
      
      if (this.newVideoItems.length > 0) {
        const firstVideo = this.newVideoItems[0]
        if (firstVideo.status === 'generating') {
          console.log('更新第一个生成中的视频')
          firstVideo.videoUrl = videoData.videoUrl
          firstVideo.thumbnail = videoData.thumbnail
          firstVideo.status = 'completed'
          firstVideo.progress = 100
          firstVideo.progressText = '生成完成'
          console.log('视频已更新为completed状态')
        }
      }
      
      // 同时刷新历史列表
      this.loadVideoHistory()
    }
  }
}
</script>

<style scoped>
.video-content {
  width: 100%;
}

.full-width-container {
  width: 100%;
  box-sizing: border-box;
  max-width: 1600px;
  margin: 0 auto;
  padding-right: 50px;
  overflow: hidden;
}

.video-list {
  width: 100%;
  margin-bottom: 120px;
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.video-item {
  background: rgba(255, 255, 255, 0.03);
  border: none;
  border-radius: 16px;
  padding: 16px;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  height: 66.67vh;
  min-height: 500px;
  max-height: 800px;
  display: flex;
  flex-direction: column;
  position: relative;
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  margin: 0;
}

.video-item:hover {
  box-shadow: 0 8px 32px rgba(0, 230, 172, 0.1);
}

.video-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: nowrap;
  width: 100%;
  box-sizing: border-box;
}

.video-info-badges {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.model-badge {
  background: rgba(0, 230, 172, 0.1);
  border: 1px solid rgba(0, 230, 172, 0.3);
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 12px;
  color: #00e6ac;
  display: flex;
  align-items: center;
  gap: 6px;
}

.info-badge {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 12px;
  color: #e6e6e6;
  display: flex;
  align-items: center;
  gap: 6px;
}

.type-badge {
  background: rgba(0, 230, 172, 0.1);
  border: 1px solid rgba(0, 230, 172, 0.3);
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 12px;
  color: #00e6ac;
  display: flex;
  align-items: center;
  gap: 6px;
}

.generate-time {
  font-size: 12px;
  color: #aaa;
  white-space: nowrap;
  margin-left: 10px;
}

/* 内容布局 */
.content-layout {
  display: flex;
  align-items: center;
  gap: 20px;
  flex: 1;
  min-height: 0;
  max-width: 100%;
  box-sizing: border-box;
}

/* 文生视频内容布局 */
.text-content-layout {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 0;
  max-width: 100%;
  box-sizing: border-box;
}

.text-video-section {
  flex: 1;
  max-width: 50%;
  min-width: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}

.image-section {
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}

.video-section {
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}

.media-box {
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.02);
  border: none;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 0;
}

/* 9:16竖图 - 居中显示，上下留边距 */
.media-box.aspect-9-16 {
  padding: 20px 0;
}

.media-box.aspect-9-16 .image-container,
.media-box.aspect-9-16 img {
  height: 100%;
  width: auto;
  max-width: 100%;
}

/* 16:9横图 - 居中显示，保持比例 */
.media-box.aspect-16-9 .image-container,
.media-box.aspect-16-9 img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* 1:1正方形 - 居中显示，保持比例 */
.media-box.aspect-1-1 .image-container,
.media-box.aspect-1-1 img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.image-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 4px;
}

.no-images {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  background: rgba(255, 255, 255, 0.02);
  border: 2px dashed #333;
  border-radius: 8px;
  text-align: center;
}

.no-images .upload-icon {
  font-size: 48px;
  margin-bottom: 12px;
  color: #666;
}

.no-images p {
  color: #666;
  font-size: 14px;
}

.arrow-section {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  min-width: 80px;
}

.arrow {
  font-size: 28px;
  color: #00e6ac;
  font-weight: bold;
  animation: arrowPulse 2s ease-in-out infinite;
}

@keyframes arrowPulse {
  0%, 100% {
    transform: scale(1);
    opacity: 0.6;
  }
  50% {
    transform: scale(1.2);
    opacity: 1;
  }
}

/* 加载容器 */
.loading-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 8px;
  z-index: 2;
}

/* 玻璃效果 */
.glass-effect {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    135deg,
    rgba(0, 230, 172, 0.05) 0%,
    rgba(0, 230, 172, 0.1) 50%,
    rgba(0, 230, 172, 0.05) 100%
  );
  backdrop-filter: blur(10px);
  z-index: 1;
}

/* 玻璃反光动画 */
.glass-shine {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(
    45deg,
    transparent 30%,
    rgba(255, 255, 255, 0.1) 50%,
    transparent 70%
  );
  animation: shine 2s infinite;
  transform: rotate(45deg);
}

@keyframes shine {
  0% {
    transform: translateX(-100%) rotate(45deg);
  }
  100% {
    transform: translateX(100%) rotate(45deg);
  }
}

/* 加载内容 */
.loading-content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 20px;
}

/* 加载动画 - 旋转圆环 */
.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(0, 230, 172, 0.2);
  border-top-color: #00e6ac;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* 加载文字 */
.loading-text {
  color: #00e6ac;
  font-size: 14px;
  font-weight: 500;
}

/* 进度条容器 */
.progress-bar-container {
  width: 100%;
  max-width: 200px;
  display: flex;
  align-items: center;
  gap: 10px;
}

/* 进度条 */
.progress-bar {
  flex: 1;
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  overflow: hidden;
}

/* 进度填充 */
.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #00e6ac, #00d09d);
  border-radius: 3px;
  transition: width 0.2s ease;
  position: relative;
  overflow: hidden;
}

.progress-fill::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

/* 进度文字 */
.progress-text {
  color: #e6e6e6;
  font-size: 12px;
  min-width: 40px;
  text-align: right;
}

.progress-text.error {
  color: #ffffff !important;
  font-size: 13px;
  min-width: auto;
  font-weight: 500;
  white-space: nowrap;
}

.result-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 进度条样式 */
.progress-container {
  position: absolute;
  bottom: 20px;
  left: 20px;
  right: 20px;
  z-index: 10;
}

.progress-bar {
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 8px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #00e6ac, #00c292);
  border-radius: 2px;
  transition: width 0.3s ease;
  position: relative;
  overflow: hidden;
}

.progress-fill::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.4) 50%,
    transparent 100%
  );
  animation: wave 1.5s ease-in-out infinite;
}

@keyframes wave {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(200%);
  }
}

.progress-text {
  font-size: 12px;
  color: #e6e6e6;
  text-align: right;
  font-family: 'Inter', sans-serif;
}

/* 生成失败状态 */
.failed-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(13, 15, 22, 0.9);
  border-radius: 8px;
  gap: 12px;
  z-index: 2;
}

.failed-icon {
  font-size: 36px;
  opacity: 0.6;
}

.failed-text {
  color: #ff6b6b;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  padding: 0 20px;
}

/* 提示词容器 */
.prompt-container {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 12px 16px;
  margin-top: 16px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.prompt-content {
  flex: 1;
  min-width: 0;
}

.prompt-container .prompt {
  margin: 0;
  font-size: 14px;
  line-height: 1.4;
  color: #e6e6e6;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.action-buttons {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.action-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 8px 12px;
  color: #aaa;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.action-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(0, 230, 172, 0.3);
  color: #00e6ac;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 230, 172, 0.2);
}

.action-btn .icon {
  font-size: 16px;
}

/* 空状态 */
.empty-state {
  text-align: center;
  padding: 80px 20px;
  color: #8892b0;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.empty-state h3 {
  font-size: 20px;
  margin-bottom: 8px;
  color: #e6f1ff;
}

.empty-state p {
  font-size: 16px;
  max-width: 400px;
  margin: 0 auto;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .video-list {
    padding: 0 20px;
    margin-bottom: 200px;
  }
  
  .video-item {
    padding: 16px;
    height: auto;
    min-height: auto;
    max-height: none;
  }
  
  .content-layout {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
  
  .image-section,
  .video-section {
    flex: none;
    width: 100%;
    height: 200px;
  }
  
  .arrow-section {
    width: 100%;
    min-width: auto;
    height: 40px;
  }
  
  .arrow {
    font-size: 18px;
  }
  
  .prompt-container {
    flex-direction: column;
  }
  
  .action-buttons {
    width: 100%;
    justify-content: center;
  }
}
</style>